import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { FilterSelect, HpButton, PageHeading, Toolbar, ToolbarSpacer } from '@hp/ui';
import { useCan } from '../api/me';
import { countMatching, useClusters, useQueueSummary } from '../features/review/queries';
import { ErrorAlert } from '../components/error-alert';
import { LoadMore } from '../components/ui/load-more';
import { ClusterTable } from '../features/review/cluster-table';
import { parseFilters, STATUSES } from '../features/review/queue-filters';
import { QueueStats } from '../features/review/queue-stats';
import { useApproveAllAuto, useBulkApprove } from '../features/review/use-bulk-approve';
import { ConfirmDialog } from '../components/confirm-dialog';

export default function ReviewList() {
  const { t } = useTranslation(['review', 'common']);
  const can = useCan();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);
  const clusters = useClusters(filters);
  const summary = useQueueSummary();
  const total = countMatching(summary.data, filters);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [bulkApprovedCount, setBulkApprovedCount] = useState<number | null>(null);
  const [approveAllOpen, setApproveAllOpen] = useState(false);

  const items = useMemo(() => clusters.data?.pages.flatMap((p) => p.items) ?? [], [clusters.data]);
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const bulkMode = can('crm.bulk_approve') && filters.tier === 'auto' && filters.status === 'pending';

  function setFilter(key: 'entityType' | 'tier' | 'status', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setSelected(new Set());
    setBulkApprovedCount(null);
  }

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const bulkApprove = useBulkApprove(itemById, (approved) => {
    setSelected(new Set());
    setBulkApprovedCount(approved);
  });

  const approveAll = useApproveAllAuto((approved) => {
    setSelected(new Set());
    setBulkApprovedCount(approved);
    setApproveAllOpen(false);
  });
  // every pending auto cluster of the selected entity type (or both) — what "approve all" would flip
  const pendingAuto = countMatching(summary.data, { entityType: filters.entityType, tier: 'auto', status: 'pending' });

  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      {bulkApprovedCount !== null && (
        <p className="bo-notice" role="status">
          {t('bulkApproved', { count: bulkApprovedCount })}
        </p>
      )}
      <ErrorAlert error={bulkApprove.error} />
      <ErrorAlert error={approveAll.error} />
      <ErrorAlert error={clusters.error} />

      <QueueStats rows={summary.data} entityType={filters.entityType} />

      <section className="hp-card">
        <Toolbar className="bo-toolbar">
          <FilterSelect
            value={filters.entityType}
            onChange={(e) => setFilter('entityType', e.target.value)}
            aria-label={t('filters.entityTypeAria')}
          >
            <option value="">{t('filters.allEntities')}</option>
            <option value="person">{t('filters.persons')}</option>
            <option value="organization">{t('filters.organizations')}</option>
          </FilterSelect>
          <FilterSelect
            value={filters.tier}
            onChange={(e) => setFilter('tier', e.target.value)}
            aria-label={t('filters.tierAria')}
          >
            <option value="">{t('filters.allTiers')}</option>
            <option value="auto">{t('filters.tierAuto')}</option>
            <option value="review">{t('filters.tierReview')}</option>
          </FilterSelect>
          <FilterSelect
            value={filters.status}
            onChange={(e) => setFilter('status', e.target.value)}
            aria-label={t('filters.statusAria')}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`common:status.${s}`)}
              </option>
            ))}
          </FilterSelect>
          <span className="bo-muted bo-mono">
            {total !== undefined
              ? t('loadedOf', { count: items.length, total })
              : clusters.hasNextPage
                ? t('loadedMore', { count: items.length })
                : t('loaded', { count: items.length })}
          </span>
          <ToolbarSpacer />
          {bulkMode && (
            <HpButton
              variant="primary"
              icon="check"
              busy={bulkApprove.isPending}
              disabled={selected.size === 0}
              onClick={() => bulkApprove.mutate([...selected])}
            >
              {t('approveSelected', { count: selected.size })}
            </HpButton>
          )}
          {bulkMode && (
            <HpButton
              icon="checkCheck"
              busy={approveAll.isPending}
              disabled={!pendingAuto || bulkApprove.isPending}
              onClick={() => setApproveAllOpen(true)}
            >
              {t('approveAllAuto', { count: pendingAuto ?? 0 })}
            </HpButton>
          )}
        </Toolbar>

        <ClusterTable
          items={items}
          filters={filters}
          bulkMode={bulkMode}
          isLoading={clusters.isLoading}
          selected={selected}
          allSelected={allSelected}
          onToggleSelected={toggleSelected}
          onSelectAll={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))}
        />
      </section>

      <ConfirmDialog
        open={approveAllOpen}
        title={t('approveAllDialog.title')}
        confirmLabel={t('approveAllDialog.confirm', { count: pendingAuto ?? 0 })}
        busy={approveAll.isPending}
        onConfirm={() => approveAll.mutate(filters.entityType)}
        onCancel={() => setApproveAllOpen(false)}
      >
        <p>
          {t('approveAllDialog.intro', {
            count: pendingAuto ?? 0,
            scope: filters.entityType ? t(`filters.${filters.entityType}s`) : t('filters.allEntities'),
          })}
        </p>
        <p>{t('approveAllDialog.when')}</p>
      </ConfirmDialog>

      {clusters.hasNextPage && (
        <div className="bo-row">
          <LoadMore
            hasNextPage={clusters.hasNextPage}
            isFetchingNextPage={clusters.isFetchingNextPage}
            onClick={() => void clusters.fetchNextPage()}
          />
        </div>
      )}
    </div>
  );
}
